/* eslint-disable no-param-reassign */
import _ from 'lodash';
import moment from 'moment';

/**
 * Check if an element (needle) is contained in an array (haystack)
 *
 * @param {(T|Array.<T>)} needle The element to search
 * @param {Array.<T>} haystack The array to search in
 */
const hasElement = (needle, haystack) => {
  if (Array.isArray(needle)) {
    return [haystack, needle].reduce((a, c) => a.filter((i) => c.includes(i))).length > 0;
  }

  return haystack.includes(needle);
};

/**
 * Check if a string is empty.
 * A bunch of white spaces is considered as an empty string.
 *
 * @param {String} string The string to check
 * @returns {Boolean}
 */
const isEmpty = (string) => !string || (typeof string === 'string' && string.trim().length === 0);

const buildFullName = (surname, name) => {
  if (surname !== undefined && name !== undefined) {
    return `${surname} ${name}`;
  }
  if (surname !== undefined && name === undefined) {
    return `${surname}`;
  }
  if (surname === undefined && name !== undefined) {
    return `${name}`;
  }
  return '-';
};

function isOldCedap(date) {
  if (!date) return true;
  const d = moment(date);
  return d.isBefore('2026-01-01');
}

function dataNotAvailableOptions(options, date) {
  if (!date) return options;
  const d = moment(date);
  if (d.isBefore('2026-02-23')) return { ...options, not_available: 'Dato non disponibile' };
  return options;
}

/**
 * Sets to null the fields that have been changed from a valid value to an "empty" value (null, undefined, "").
 * Fields that remain with the initial value (form default) and that don't have a loaded value (DB/API)
 * are removed from the resulting object (are not sent).
 * @param {Object} current - Current values
 * @param {Object} loaded - Loaded values (DB/API)
 * @param {Object} initial - Initial values (Form default)
 * @param {boolean} [preserve=false] - If true, keeps empty fields (e.g. "") even if they match the initial (except resets).
 * @returns {Object} Resulting object
 */
function setChangedToNull(current, loaded = {}, initial = {}, preserve = false) {
  if (!_.isPlainObject(current)) return current;
  return _.transform(current, (result, currVal, key) => {
    const loadVal = _.get(loaded, key);
    const initVal = _.get(initial, key);
    if (_.isPlainObject(currVal)) {
      result[key] = setChangedToNull(currVal, loadVal, initVal, preserve);
      return;
    }
    const hasValue = (val) => !_.isNil(val) && val !== '';
    if (hasValue(loadVal) && !hasValue(currVal)) {
      result[key] = null;
      return;
    }
    const isUnchangedDefault = _.isEqual(currVal, initVal) && !hasValue(loadVal);
    if (isUnchangedDefault) {
      if (preserve) result[key] = currVal;
      return;
    }
    result[key] = currVal;
  });
}

export {
  hasElement,
  isEmpty,
  buildFullName,
  isOldCedap,
  setChangedToNull,
  dataNotAvailableOptions,
};
